import axios from 'axios'
import * as storage from '../Services/storage.jsx'
import {getApiUrl, getHubspotApiUrl} from './helpers.jsx'

const HB_TIMEOUT = 5 * 1000 // in ms
const HUBSPOT_API_URL = getHubspotApiUrl()
const kindToType = {
  postediting: 'economy',
  translation: 'professional',
  'translation+revision': 'premium',
}

export const hbapi_trackQuotesFromLocalStorage = async (email) => {
  const storageQuotes = storage.hbapi_getAllInstantQuote()
  // instantly clean local storage after reading
  storage.hbapi_deleteInstantQuotes()

  if (Object.keys(storageQuotes || {}).length === 0) return

  const body = Object.entries(storageQuotes || {})
    .map(([quoteCode, quote]) => {
      const selected_delivery_date = quote.deliveryDate.isAuto
        ? 'auto'
        : quote.deliveryDate.date?.toISOString()
      const target_languages = quote.targetLanguages.map(({id}) => id)
      let solutionType = quote.quoteKind
        ? kindToType[quote.quoteKind] || 'professional'
        : undefined

      if (!quoteCode) return null

      return {
        email,
        source: quote.sourceLanguage.id,
        target: target_languages,
        words: quote.wordCount,
        subject: quote.subject.id,
        selectedDeliveryDate: selected_delivery_date,
        quoteCode,
        premiumPrice: quote.premiumPrice || 0,
        professionalPrice: quote.professionalPrice || 0,
        economyPrice: quote.economyPrice || 0,
        projectId: quote.id,
        amount: quote.price,
        solutionType,
        timestamp: quote.timestamp,
      }
    })
    .filter(Boolean)

  try {
    await axios.post(getApiUrl() + `/deals/stored-quotes`, body, {
      timeout: HB_TIMEOUT,
    })
  } catch (error) {
    console.error(error)
  }
}

/**
 * Tracks the quote when the user press on "show prices" to get a quotation, creating a deal on HubSpot
 * @param email {string} the email of the logged customer or the newly registered one
 * @param quoteCode {string} the quoteLink that identifies the quotation
 * @param quote {Object} the quotation in the first step if it's not a new user,
 *                       otherwise they will be linked in "new_purchase"
 * @return {Promise<Object>}
 */
export const hbapi_trackQuote = async (email, quoteCode, quote) => {
  if (!HUBSPOT_API_URL) return Promise.resolve()
  const selected_delivery_date = quote.deliveryDate.isAuto
    ? 'auto'
    : quote.deliveryDate.date?.toISOString()

  const target_languages = quote.targetLanguages.map(({id}) => id) // collect id of target languages in an array and make it a string

  const body = {
    email,
    source: quote.sourceLanguage.id,
    target: target_languages,
    words: quote.wordCount,
    subject: quote.subject.id,
    selectedDeliveryDate: selected_delivery_date,
    quoteCode,
    premiumPrice: quote.premiumPrice || 0,
    professionalPrice: quote.professionalPrice || 0,
    economyPrice: quote.economyPrice || 0,
    timestamp: new Date().toISOString(),
  }

  try {
    await axios.post(getApiUrl() + '/deals/new-quote', body, {
      timeout: HB_TIMEOUT,
    })
    return quote
  } catch (error) {
    console.error(error)
  }
}

/**
 * When the user choose one of the offers, the deal must be updated
 * @param email {string}
 * @param quoteCode {string}
 * @param quote {Object}
 * @return {Promise<void>}
 */
export const hbapi_chooseProject = async (email, quoteCode, quote) => {
  if (!HUBSPOT_API_URL) return Promise.resolve()
  const solutionType = kindToType[quote.quoteKind] || 'economy'
  const body = {
    quoteCode,
    email,
    projectId: quote.id,
    amount: quote.price,
    solutionType,
  }

  try {
    await axios.put(
      getApiUrl() + `/deals/${quoteCode}/quote-selected`,
      body,
      {timeout: HB_TIMEOUT},
    )
  } catch (error) {
    console.error(error)
  }
}

/**
 * Notifies the just occurred purchase updating the deal to the last stage
 * @param email {string} the project id
 * @param pid {number} the project id
 * @param amount {number} provided email in the registration form
 * @param projectEmail {string} provided email in the registration form
 * @param projectName {string} name of the project (if provided by the user)
 * @param newUser {Object} if the user just registered it will be added in the deal
 * @return {Promise<void>}
 */
export const hbapi_trackPurchase = async (
  email,
  pid,
  amount,
  projectEmail,
  projectName,
  newUser = null,
) => {
  if (!HUBSPOT_API_URL) return Promise.resolve()
  try {
    const body = {email, projectId: pid, projectName: projectName || '', amount}
    if (newUser) {
      body.customer = {email: newUser.email, projectEmail}
      const nameSplit = newUser.name.split(' ')
      body.customer.firstName =
        nameSplit.length > 2
          ? nameSplit.splice(0, 2).join(' ')
          : nameSplit.splice(0, 1).join(' ')
      body.customer.lastName = nameSplit.join(' ')
      body.customer.lang = document.documentElement.lang
      body.customer.city = newUser.city
      body.customer.country = newUser.country.name
      body.customer.company = newUser.companyName
      body.customer.phone = newUser.telephone
    }
    await axios.put(getApiUrl() + `/deals/${pid}/new-purchase`, body, {
      timeout: HB_TIMEOUT,
    })
  } catch (error) {
    console.error(error)
  }
}
